import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

// Universal slug generation
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '');
};

// Create SEO-friendly URL for discussions
export const createDiscussionUrl = (category: string, id: string, title: string): string => {
  const slug = generateSlug(title);
  return `/forum/${category}/${id}-${slug}`;
};

// Create SEO-friendly URL for dramas (genre+slug format)
export const createDramaUrl = (genreSlug: string, titleSlug: string): string => {
  return `/dramas/${genreSlug}-${titleSlug}`;
};

// Create SEO-friendly URL for creators (slug-only, no ID)
export const createCreatorUrl = (slug: string): string => {
  return `/creators/${slug}`;
};

// Create SEO-friendly URL for groups
export const createGroupUrl = (sequentialId: number, name: string): string => {
  const slug = generateSlug(name);
  const paddedId = `group-${String(sequentialId).padStart(3, '0')}`;
  return `/groups/${paddedId}-${slug}`;
};

// Create SEO-friendly URL for group posts
export const createGroupPostUrl = (groupId: string, groupName: string, postId: string, postTitle: string): string => {
  const groupSlug = generateSlug(groupName);
  const postSlug = generateSlug(postTitle);
  return `/groups/${groupId}-${groupSlug}/post/${postId}-${postSlug}`;
};

// Create SEO-friendly URL for AI projects (category+slug format)
export const createAIProjectUrl = (slug: string): string => {
  return `/dramas/project/${slug}`;
};

// Create user profile URL from author data (uses slug for bot users)
export const createUserProfileUrl = (authorId: string, username: string): string => {
  const slug = generateSlug(username);
  return `/user/${slug}`;
};

// Parse URL parameter to extract ID and slug
export const parseUrlParam = (urlParam: string | undefined): { id: string; slug: string } => {
  if (!urlParam) return { id: '', slug: '' };
  
  // Handle format: "id-slug-text"
  const firstDashIndex = urlParam.indexOf('-');
  if (firstDashIndex === -1) {
    return { id: urlParam, slug: '' };
  }
  
  const id = urlParam.substring(0, firstDashIndex);
  const slug = urlParam.substring(firstDashIndex + 1);
  
  return { id, slug };
};

// Extract just the ID from URL param (handles formats like "group-001-name" or just "group-001")
export const extractIdFromParam = (urlParam: string | undefined): string => {
  if (!urlParam) return '';
  
  // Check if it matches pattern like "group-001-..." or "creator-001-..."
  const prefixMatch = urlParam.match(/^(group|creator|drama|disc|gpost)-(\d+)/);
  if (prefixMatch) {
    return `${prefixMatch[1]}-${prefixMatch[2]}`;
  }
  
  // Fallback to original logic
  const { id } = parseUrlParam(urlParam);
  return id;
};

// Extract numeric ID from URL param (for AI projects with numeric IDs)
export const extractNumericIdFromParam = (urlParam: string | undefined): number => {
  if (!urlParam) return 0;
  const { id } = parseUrlParam(urlParam);
  const num = parseInt(id, 10);
  return isNaN(num) ? 0 : num;
};

// Extract group sequential ID from URL param (handles formats like "group-001-name")
export const extractGroupIdFromParam = (urlParam: string | undefined): number => {
  if (!urlParam) return 0;
  
  // Match pattern like "group-001-..."
  const match = urlParam.match(/^group-(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  
  return 0;
};

// Extract slug from AI project URL param (handles formats like "category-title-slug")
export const extractAIProjectSlugFromParam = (urlParam: string | undefined): string => {
  if (!urlParam) return '';
  // Return the full slug as-is (e.g., "sci-fi-the-last-algorithm")
  return urlParam;
};

// Custom hook for universal navigation
export const useUniversalNavigation = () => {
  const navigate = useNavigate();
  const params = useParams();

  const navigateToDiscussion = useCallback((category: string, id: string, title: string) => {
    const url = createDiscussionUrl(category, id, title);
    navigate(url);
  }, [navigate]);

  const navigateToDrama = useCallback((genreSlug: string, titleSlug: string) => {
    const url = createDramaUrl(genreSlug, titleSlug);
    navigate(url);
  }, [navigate]);

  const navigateToCreator = useCallback((slug: string) => {
    const url = createCreatorUrl(slug);
    navigate(url);
  }, [navigate]);

  const navigateToGroup = useCallback((id: string, name: string) => {
    const url = createGroupUrl(id, name);
    navigate(url);
  }, [navigate]);

  const navigateToGroupPost = useCallback((groupId: string, groupName: string, postId: string, postTitle: string) => {
    const url = createGroupPostUrl(groupId, groupName, postId, postTitle);
    navigate(url);
  }, [navigate]);

  const navigateToCategory = useCallback((category: string) => {
    navigate(`/forum/${category}`);
  }, [navigate]);

  const currentId = useMemo(() => {
    const urlParam = params.urlParam || params.id || params.groupId || params.postId;
    return extractIdFromParam(urlParam);
  }, [params]);

  return {
    navigateToDiscussion,
    navigateToDrama,
    navigateToCreator,
    navigateToGroup,
    navigateToGroupPost,
    navigateToCategory,
    currentId,
    params
  };
};

// Breadcrumb generation
export interface BreadcrumbItem {
  label: string;
  href: string;
}

export const generateBreadcrumbs = (
  type: 'discussion' | 'drama' | 'creator' | 'group' | 'groupPost' | 'aiProject',
  data: { 
    category?: string; 
    categoryName?: string; 
    title?: string; 
    name?: string;
    groupId?: string;
    groupName?: string;
  }
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', href: '/' }
  ];

  switch (type) {
    case 'discussion':
      breadcrumbs.push({ label: 'Forum', href: '/forum' });
      if (data.category && data.categoryName) {
        breadcrumbs.push({ label: data.categoryName, href: `/forum/${data.category}` });
      }
      if (data.title) {
        breadcrumbs.push({ label: data.title, href: '#' });
      }
      break;
    case 'drama':
      breadcrumbs.push({ label: 'Dramas', href: '/dramas' });
      if (data.title) {
        breadcrumbs.push({ label: data.title, href: '#' });
      }
      break;
    case 'creator':
      breadcrumbs.push({ label: 'Creators', href: '/creators' });
      if (data.name) {
        breadcrumbs.push({ label: data.name, href: '#' });
      }
      break;
    case 'group':
      breadcrumbs.push({ label: 'Groups', href: '/groups' });
      if (data.name) {
        breadcrumbs.push({ label: data.name, href: '#' });
      }
      break;
    case 'groupPost':
      breadcrumbs.push({ label: 'Groups', href: '/groups' });
      if (data.groupId && data.groupName) {
        breadcrumbs.push({ label: data.groupName, href: createGroupUrl(data.groupId, data.groupName) });
      }
      if (data.title) {
        breadcrumbs.push({ label: data.title, href: '#' });
      }
      break;
    case 'aiProject':
      breadcrumbs.push({ label: 'Dramas', href: '/dramas' });
      breadcrumbs.push({ label: 'AI Gallery', href: '/dramas?aidramasgallery' });
      if (data.title) {
        breadcrumbs.push({ label: data.title, href: '#' });
      }
      break;
  }

  return breadcrumbs;
};

export default useUniversalNavigation;
